import React, { useState } from 'react';
import { ArrowLeft, ChevronRight, Shield, Cloud, Brain, Smartphone, Database, Zap, DollarSign, Activity } from 'lucide-react';
import { CostReport } from './CostReport';
import { OperationalDashboard } from './OperationalDashboard';
import './HelpPage.css';

interface HelpPageProps {
    onNavigate: (view: any) => void;
}

type HelpSection = 'index' | 'architecture' | 'privacy' | 'features' | 'costs' | 'operations';

export const HelpPage: React.FC<HelpPageProps> = ({ onNavigate }) => {
    const [section, setSection] = useState<HelpSection>('index');

    if (section === 'costs') {
        return <CostReport onBack={() => setSection('index')} />;
    }

    if (section === 'operations') {
        return (
            <div className="help-page">
                <div className="help-header">
                    <button className="back-button" onClick={() => setSection('index')}>
                        <ArrowLeft size={16} /> Back
                    </button>
                    <h2>Operational Dashboard</h2>
                </div>
                <OperationalDashboard />
            </div>
        );
    }

    if (section === 'architecture') {
        return (
            <div className="help-page">
                <div className="help-header">
                    <button className="back-button" onClick={() => setSection('index')}>
                        <ArrowLeft size={16} /> Back
                    </button>
                    <h2>How It Works</h2>
                </div>

                <div className="architecture-content">
                    <p className="arch-intro">
                        Your health data flows through a secure, private pipeline — from your phone to AI-powered insights. 
                        Here's what happens behind the scenes.
                    </p>

                    <div className="arch-diagram">
                        <div className="arch-layer">
                            <div className="arch-icon"><Smartphone size={28} /></div>
                            <div className="arch-label">
                                <strong>You</strong>
                                <span>Web app, SMS, or Apple Health</span>
                            </div>
                        </div>
                        <div className="arch-arrow">↓</div>

                        <div className="arch-layer">
                            <div className="arch-icon"><Shield size={28} /></div>
                            <div className="arch-label">
                                <strong>Authentication</strong>
                                <span>Cognito verifies your identity</span>
                            </div>
                        </div>
                        <div className="arch-arrow">↓</div>

                        <div className="arch-layer">
                            <div className="arch-icon"><Cloud size={28} /></div>
                            <div className="arch-label">
                                <strong>API Gateway</strong>
                                <span>Encrypted HTTPS endpoints</span>
                            </div>
                        </div>
                        <div className="arch-arrow">↓</div>

                        <div className="arch-layer">
                            <div className="arch-icon"><Zap size={28} /></div>
                            <div className="arch-label">
                                <strong>Processing</strong>
                                <span>Serverless functions handle your data</span>
                            </div>
                        </div>
                        <div className="arch-arrow">↓</div>

                        <div className="arch-layer">
                            <div className="arch-icon"><Database size={28} /></div>
                            <div className="arch-label">
                                <strong>Storage</strong>
                                <span>DynamoDB — encrypted at rest</span>
                            </div>
                        </div>
                        <div className="arch-arrow">↓</div>

                        <div className="arch-layer highlight">
                            <div className="arch-icon"><Brain size={28} /></div>
                            <div className="arch-label">
                                <strong>AI Analysis</strong>
                                <span>Bedrock generates insights from your patterns</span>
                            </div>
                        </div>
                    </div>

                    <div className="arch-details">
                        <h3>Data Input (3 ways in)</h3>
                        <ul>
                            <li><strong>Web app</strong> — Log meals, bathroom visits, weight, supplements, sleep, mood, and water intake directly</li>
                            <li><strong>SMS</strong> — Text your Twilio number in plain English ("had eggs and coffee at 8am") and AI parses it</li>
                            <li><strong>Apple Health</strong> — Weight and blood pressure sync automatically via iOS Shortcut</li>
                        </ul>

                        <h3>What the AI Does</h3>
                        <ul>
                            <li><strong>Event Analysis</strong> — Each logged event gets an AI observation (e.g., "Bristol 4 is ideal, good hydration signal")</li>
                            <li><strong>Daily Summaries</strong> — End-of-day synthesis connecting your meals, digestion, mood, and activity</li>
                            <li><strong>Home Context</strong> — Real-time health score, next action suggestions, and a personalized checklist</li>
                            <li><strong>Correlation</strong> — Links related events (e.g., a meal to a bathroom visit 4 hours later)</li>
                            <li><strong>Knowledge Base</strong> — Research articles inform the AI's suggestions based on your specific conditions</li>
                            <li><strong>Chat</strong> — Ask questions about your health history and get answers grounded in your actual data</li>
                        </ul>

                        <h3>Security & Privacy</h3>
                        <ul>
                            <li>All data encrypted in transit (TLS) and at rest (AWS KMS)</li>
                            <li>Authentication via AWS Cognito — your password never touches the app</li>
                            <li>Single-tenant — your data is isolated, not shared with other users</li>
                            <li>AI processing via Amazon Bedrock — your data is not used to train models</li>
                            <li>No third-party analytics or tracking</li>
                            <li>Data auto-expires after 2 years (configurable TTL)</li>
                        </ul>

                        <h3>Tech Stack</h3>
                        <div className="tech-stack">
                            <span className="tech-badge">React</span>
                            <span className="tech-badge">AWS Lambda</span>
                            <span className="tech-badge">DynamoDB</span>
                            <span className="tech-badge">API Gateway</span>
                            <span className="tech-badge">Cognito</span>
                            <span className="tech-badge">Bedrock (Claude)</span>
                            <span className="tech-badge">CloudFront</span>
                            <span className="tech-badge">S3</span>
                            <span className="tech-badge">Twilio</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Index / landing
    return (
        <div className="help-page">
            <div className="help-header">
                <h2>Help</h2>
            </div>

            <div className="help-menu">
                <button className="help-menu-item" onClick={() => setSection('architecture')}>
                    <div className="menu-item-content">
                        <Cloud size={20} />
                        <div>
                            <strong>How It Works</strong>
                            <span>Architecture, data flow, and what the AI does</span>
                        </div>
                    </div>
                    <ChevronRight size={16} />
                </button>
                <button className="help-menu-item" onClick={() => setSection('costs')}>
                    <div className="menu-item-content">
                        <DollarSign size={20} />
                        <div>
                            <strong>Cost Report</strong>
                            <span>Monthly AWS costs breakdown</span>
                        </div>
                    </div>
                    <ChevronRight size={16} />
                </button>
                <button className="help-menu-item" onClick={() => setSection('operations')}>
                    <div className="menu-item-content">
                        <Activity size={20} />
                        <div>
                            <strong>Operational Dashboard</strong>
                            <span>Lambda invocations, errors, and system health</span>
                        </div>
                    </div>
                    <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );
};
